import { orderBy } from "lodash";
import { DateTime } from "luxon";
import * as React from "react";
import { connect } from "react-redux";
import { Select, Stack, Text, TextInput } from "@mantine/core";
import { IState } from "../../../data/reducers/state";
import { IAssociation } from "../../../types/association/Association";
import { IShift } from "../../../types/shifts/Shift";
import { toDateTime } from "../../../util/date+helper";

export enum ShiftSelectorMode {
  supervisable = "supervisable",
}

export interface IShiftSessionKey {
  id: string | null;
  shiftId: string;
  sessionId: string | null;
}

interface IShiftSelectorProps {
  shiftId: string | null;
  sessionId: string | null;

  shiftIds?: string[];
  mode?: ShiftSelectorMode;

  onChange: (key: Partial<IShiftSessionKey>) => void;

  shifts: { [shiftId: string]: IShift };
  associations: { [associationId: string]: IAssociation };
}

interface IShiftSelectorState { }

class ShiftSelector extends React.PureComponent<
  IShiftSelectorProps,
  IShiftSelectorState
> {
  constructor(props, content) {
    super(props, content);

    this.selectShiftsOnChange = this.selectShiftsOnChange.bind(this);
    this.selectDateOnChange = this.selectDateOnChange.bind(this);
  }

  selectShiftsOnChange(event) {
    if (!event.target.value) {
      return;
    }
    const shiftId = event.target.value;
    const session: Partial<IShiftSessionKey> = { shiftId };

    const now = DateTime.now();
    const nextSessions = this.props.shifts[shiftId].sessions.filter(
      (d) => toDateTime(d.finish) > now,
    );
    if (nextSessions.length > 0) {
      session.sessionId = nextSessions[0]._id;
      session.id = session.shiftId + session.sessionId;
    }

    this.props.onChange(session);
  }

  selectDateOnChange(event) {
    const { shiftId } = this.props;

    if (!shiftId) return alert("An internal error has ocurred");

    const sessionId = event.target.value;
    const id = shiftId + sessionId;

    this.props.onChange({ shiftId, sessionId, id });
  }

  render() {
    const { shiftId, sessionId, shiftIds, shifts, associations } = this.props;
    if (!shiftIds) return <div />;

    const selectedShiftSessionId = sessionId || undefined;
    const shift: IShift | null = shiftId ? shifts[shiftId] : null;

    const shiftIdIsInList: boolean =
      !!shift && (shiftIds || []).filter((s) => s === shiftId).length > 0;

    const sortedShiftIds = orderBy(
      shiftIds,
      (s) =>
        `${shifts[s]?.associationId}-${new String(
          shifts[s]?.ordering?.value || 100,
        ).padStart(4, "0")}-${shifts[s]?.start}`,
      "asc",
    );

    const showAssociations =
      Array.from(new Set(shiftIds.map((s) => shifts[s]?.associationId)))
        .length > 1;
    return (
      <div>
        <Stack gap="sm">
          <Text size="sm" fw={500}>Shift Name</Text>
          <Select
            onChange={(value) => {
              if (value) {
                this.selectShiftsOnChange({ target: { value } });
              }
            }}
            value={shiftId || ""}
            placeholder="Select a shift"
            data={[
              ...(!shiftId ? [{ value: "", label: "Select a shift" }] : []),
              ...sortedShiftIds.map((shiftId) => ({
                value: shiftId,
                label: `${shiftId in shifts ? shifts[shiftId].name : "Unknown"}${showAssociations ? ` (${associations[shifts[shiftId]?.associationId]?.name})` : ""}`
              }))
            ]}
          />
        </Stack>
        {shift && (
          <Stack gap="sm">
            <Text size="sm" fw={500}>Shift Date</Text>
            <Select
              onChange={(value) => {
                if (value) {
                  this.selectDateOnChange({ target: { value } });
                }
              }}
              value={selectedShiftSessionId}
              placeholder="Select a session"
              data={[
                ...(!selectedShiftSessionId ? [{ value: "", label: "Select a session" }] : []),
                ...(!shiftIdIsInList ? [{
                  value: shift._id,
                  label: toDateTime(shift.start).toLocaleString(DateTime.DATE_FULL)
                }] : []),
                ...(shift?.sessions || []).map((val) => ({
                  value: val._id,
                  label: toDateTime(val.start).toLocaleString(DateTime.DATE_FULL)
                }))
              ]}
            />
          </Stack>
        )}
      </div>
    );
  }
}

// ShiftSelector.propTypes = {
// 	shiftId: PropTypes.string,
// 	sessionId: PropTypes.string,
// 	mode: PropTypes.string,

// 	onChange: PropTypes.func.isRequired,
// 	shifts: PropTypes.object.isRequired,
// 	shiftIds: PropTypes.array
// };

function mapStateToProps(state: IState, ownProps) {
  const mode = ownProps.mode || ShiftSelectorMode.supervisable;
  return {
    shiftIds: ownProps.shiftIds || state.shifts[mode] || [],
    shifts: state.shifts.map,
    associations: state.associations.map,
  };
}

function mapDispatchToProps() {
  return {};
}

export default connect(mapStateToProps, mapDispatchToProps)(ShiftSelector);
